import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import BottomNav from '../components/BottomNav';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Bell, Info } from 'lucide-react';
import { toast } from 'sonner';

function NotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/notifications');
      setNotifications(response.data);
      
      // Mark all unread notifications as read
      const unreadNotifications = response.data.filter(n => !n.read);
      if (unreadNotifications.length > 0) {
        // Mark each unread notification as read
        await Promise.all(
          unreadNotifications.map(notification =>
            api.put(`/notifications/${notification.id}/read`).catch(err => {
              console.error('Failed to mark notification as read:', err);
            })
          )
        );
      }
    } catch (error) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-6 shadow-lg">
        <div className="max-w-7xl mx-auto px-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4 text-emerald-100 hover:text-white hover:bg-emerald-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold mb-2">Notifications</h1>
          <p className="text-emerald-100 text-sm">Stay updated on procurement law changes</p>
        </div>
      </div>

      {/* Update Schedule Info */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-4">
          <div className="flex gap-3">
            <Info className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <h3 className="font-semibold text-blue-900 dark:text-blue-200 mb-1">Update Schedule</h3>
              <p className="text-sm text-blue-800 dark:text-blue-300">
                Changes and updates to this library are reflected quarterly within the first 10 days of <strong>April, July, October</strong> of the current year and <strong>January</strong> of the next year, or as often as necessary, especially if there are substantial changes to procurement laws and regulations.
              </p>
            </div>
          </div>
        </div>

        {/* Notifications List */}
        {loading ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading notifications...</div>
        ) : notifications.length === 0 ? (
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-8 text-center">
            <Bell className="mx-auto text-gray-400 dark:text-gray-600 mb-3" size={48} />
            <p className="text-gray-600 dark:text-gray-400">No notifications at this time</p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">Check back for updates on procurement laws</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className="bg-white dark:bg-slate-800 rounded-lg shadow p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex gap-3">
                  <Bell className="text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-1" size={20} />
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-1">{notification.title}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{notification.message}</p>
                    <span className="text-xs text-gray-500">
                      {new Date(notification.created_at).toLocaleDateString('en-PH', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}

export default NotificationsPage;
